/**
 * Florida SEO Keyword + Geo Registry
 * =========================================================================
 * Single source of truth for the Florida-wide SEO programmatic landing
 * pages. Consumed by:
 *   - /app/frontend/src/seo/generateFloridaMetadata.js (per-page metadata)
 *   - /app/frontend/src/components/seo/FloridaServicePage.jsx (template)
 *   - /app/backend/routes/sitemap.py (sitemap.xml + robots.txt)
 *
 * Editing notes:
 *   • Every URL here is canonical https://washpros.io/... — do NOT add
 *     defensive domains. Alternate domains only 301 → washpros.io.
 *   • Service "slug" is the URL segment, "serviceType" is the
 *     Schema.org Service.serviceType value, "label" is the human title.
 *   • All keyword lists are USED FOR INTERNAL TARGETING ONLY. We do NOT
 *     emit `<meta name="keywords">` (modern Google ignores it and it
 *     looks spammy). Keywords drive page-copy variation only.
 *   • City lists are deduped + alphabetized per service so the sitemap
 *     is deterministic.
 *
 * 2026-05-11 Phase 7 — Florida SEO build-out.
 */

export const CANONICAL_DOMAIN = "https://washpros.io";
export const ORG_NAME = "WashPros";
export const ORG_LEGAL = "WashPros Global Services LLC";
export const ORG_BRAND_TITLE = "WashPros™";
export const ORG_TAGLINE = "The Car Wash Concierge Company™";
export const ORG_FLEET_BRAND = "WashPros Fleet™";
export const ORG_WAX_SHINE = "Wax and Shine Headquarters™";

/* ─── Service catalog ─────────────────────────────────────────────
   Each service maps to:
     - one statewide landing page  /florida/{slug}
     - one per-city landing page   /florida/{citySlug}/{slug}
   The slug list is intentionally NOT in URL-prefix collision with
   the legacy /mobile-car-wash etc. landing pages — those continue to
   serve generic intent; /florida/* serves geo-qualified intent.
   ───────────────────────────────────────────────────────────────── */
export const SERVICES = [
  // Customer / consumer services
  {
    slug: "mobile-car-wash",
    label: "Mobile Car Wash",
    serviceType: "Mobile Car Wash",
    audience: "customer",
    bucket: "vehicle",
    icon: "Car",
    keywords: [
      "mobile car wash", "mobile car wash near me", "on-demand car wash",
      "driveway car wash", "at-home car wash", "same-day car wash",
      "hand wash", "professional car wash", "premium car wash",
      "eco-friendly mobile car wash", "waterless car wash", "car wash app",
      // Convenience / location intent (Rick T+34 Phase 7b cluster)
      "same-day mobile car wash", "last-minute car wash",
      "car wash at work", "office car wash service",
      "workplace car wash", "apartment car wash service",
      "condo car wash service", "HOA car wash service",
      "hotel valet car wash", "airport car wash",
      "mobile wash subscription", "monthly car wash plan",
    ],
    relatedServices: ["mobile-detailing", "fleet-washing", "specialty-vehicle-detailing"],
    faqs: [
      {
        q: "Does WashPros come to my home in {city}?",
        a: "Yes — approved WashPros mobile teams travel to homes, offices, condos, and job sites across {city}, FL with their own water, equipment, and pH-balanced soaps. Booking is online; the team arrives in the time window you select.",
      },
      {
        q: "Is the wash safe for ceramic-coated cars?",
        a: "All approved WashPros vendors are trained in contact-safe wash methods, two-bucket protocols, and ceramic-coating-friendly chemistry. You can flag a ceramic coating during booking so the vendor uses the right pH-neutral shampoo.",
      },
      {
        q: "How fast can a mobile car wash arrive in {city}?",
        a: "{city} is one of our actively-staffed Florida service areas. Most standard washes can be filled within the day; priority broadcast routes the request to all available vendors for the fastest pickup.",
      },
      {
        q: "Do you offer same-day mobile car wash in {city}?",
        a: "Yes — same-day requests in {city}, FL are common. WashPros broadcasts your booking to every approved vendor near you so the first available team can grab the job, usually within a few hours during business hours.",
      },
      {
        q: "Can WashPros wash my car at the office or apartment complex in {city}?",
        a: "Yes — WashPros vendors regularly service offices, apartment complexes, HOAs, condos, and hotel valet lots in {city}, FL. The vendor brings their own water and works around your parking restrictions.",
      },
    ],
  },
  {
    slug: "mobile-detailing",
    label: "Mobile Detailing",
    serviceType: "Mobile Detailing",
    audience: "customer",
    bucket: "vehicle",
    icon: "Sparkles",
    keywords: [
      "mobile detailing", "mobile detailing near me", "car detailing near me",
      "auto detailing near me", "interior detailing", "exterior detailing",
      "wash and wax", "luxury car detailing", "ceramic-safe wash",
      "premium mobile detailing", "book mobile detailing", "car wash concierge",
      // Deep-detailing add-ons (Rick T+34 Phase 7b cluster)
      "clay bar treatment", "ceramic coating prep", "paint protection prep",
      "headlight restoration", "engine bay cleaning",
      "leather seat cleaning", "leather conditioning",
      "odor removal car detailing", "pet hair removal car detailing",
      "smoke smell removal car", "stain removal car seats",
      "luxury car detailing", "exotic car detailing", "classic car detailing",
      "black car detailing",
      "car detailing gift card", "car wash gift card",
    ],
    relatedServices: ["mobile-car-wash", "marine-detailing", "rv-detailing"],
    faqs: [
      {
        q: "What's the difference between a wash and a detail?",
        a: "A wash cleans exterior surfaces. A detail goes deeper: clay-bar decontamination, interior vacuum + steam, leather conditioning, plastic + vinyl dressing, and optional wash-and-wax or ceramic-prep finishing.",
      },
      {
        q: "Do you offer interior detailing in {city}?",
        a: "Yes. WashPros vendors in {city}, FL offer interior detailing packages with vacuum, steam clean, leather conditioning, headliner spot-cleaning, and odor neutralization.",
      },
      {
        q: "Can you remove pet hair, smoke smell, or stains from my seats in {city}?",
        a: "Yes — approved WashPros detailers in {city}, FL offer pet-hair removal, odor neutralization, smoke-smell remediation, and stain extraction for fabric and leather seats. Flag the issue during booking so the vendor brings the right equipment.",
      },
      {
        q: "Do you do clay-bar treatment, ceramic-coating prep, or headlight restoration?",
        a: "Yes — these are popular add-ons in {city}, FL. Clay-bar decontamination, ceramic-coating prep, paint-protection prep, headlight restoration, and engine-bay cleaning can be selected when you book a detail package.",
      },
    ],
  },
  {
    slug: "fleet-washing",
    label: "Fleet Washing",
    serviceType: "Fleet Vehicle Washing",
    audience: "fleet",
    bucket: "fleet",
    icon: "Truck",
    brand: "fleet",
    keywords: [
      "fleet washing", "fleet vehicle cleaning", "commercial fleet washing",
      "mobile fleet wash", "delivery vehicle washing", "rental car cleaning",
      "dealership lot washing", "municipal fleet washing", "fleet detailing",
      "WashPros Fleet", "corporate fleet cleaning", "commercial car wash service",
      // Commercial vertical clusters (Rick T+34 Phase 7b)
      "box truck washing", "sprinter van detailing",
      "delivery van washing", "Amazon van washing",
      "food truck washing", "semi truck detailing",
      "shuttle bus washing", "school van cleaning", "church bus washing",
      "limo detailing", "party bus cleaning",
      "police fleet washing", "municipal fleet washing",
      "medical transport vehicle cleaning",
      "fleet wash subscription", "commercial pressure washing for vehicles",
    ],
    relatedServices: ["mobile-car-wash", "mobile-detailing", "vendor-opportunities"],
    faqs: [
      {
        q: "Can WashPros Fleet handle a 50-vehicle delivery fleet in {city}?",
        a: "Yes — WashPros Fleet™ coordinates approved vendors with the headcount and equipment to wash 50+ vehicle fleets onsite, including delivery vans, rental cars, dealership lots, and corporate motor pools across {city}, FL.",
      },
      {
        q: "Do you bill monthly or per-wash?",
        a: "Fleet partners can request flat-rate monthly contracts, per-wash pricing, or hybrid arrangements. We coordinate the schedule, the approved vendor, and consolidated invoicing through one point of contact.",
      },
      {
        q: "What kinds of commercial vehicles do you wash in {city}?",
        a: "WashPros Fleet™ in {city}, FL services box trucks, sprinter vans, delivery vans (including Amazon-style routes), food trucks, shuttle buses, school vans, church buses, limos, party buses, medical-transport vehicles, and municipal / police fleets.",
      },
    ],
  },
  {
    slug: "marine-detailing",
    label: "Marine Detailing",
    serviceType: "Marine Detailing",
    audience: "customer",
    bucket: "marine",
    icon: "Anchor",
    keywords: [
      "marine detailing", "marina detailing services", "dockside boat wash",
      "boat wash and wax", "boat ceramic coating prep", "salt removal wash",
      "hull cleaning coordination", "interior cabin detailing",
      "boat upholstery cleaning",
    ],
    relatedServices: ["boat-washing", "boat-detailing", "yacht-detailing"],
    faqs: [
      {
        q: "Can a marine detailer come to my dock in {city}?",
        a: "Yes — approved marine detailers in {city}, FL travel to private docks, marinas, and boat clubs with all necessary equipment for salt removal, wash-and-wax, and dockside detailing.",
      },
      {
        q: "Do you handle salt removal after offshore trips?",
        a: "Salt removal is one of the most common WashPros marine bookings. Vendors use marine-safe rinse-aids and microfiber drying to neutralize salt before it bakes into gel coat.",
      },
    ],
  },
  {
    slug: "boat-washing",
    label: "Boat Washing",
    serviceType: "Boat Washing",
    audience: "customer",
    bucket: "marine",
    icon: "Sailboat",
    keywords: [
      "boat wash", "boat washing", "mobile boat wash", "boat exterior cleaning",
      "salt removal wash", "pontoon boat cleaning", "fishing boat washing",
    ],
    relatedServices: ["marine-detailing", "boat-detailing", "yacht-detailing"],
    faqs: [
      {
        q: "How often should I wash my boat in saltwater Florida?",
        a: "After every saltwater outing if possible. {city}, FL saltwater can pit gel coat and corrode metal hardware within days. WashPros vendors offer rapid post-trip washes to neutralize salt and inspect for damage.",
      },
    ],
  },
  {
    slug: "boat-detailing",
    label: "Boat Detailing",
    serviceType: "Boat Detailing",
    audience: "customer",
    bucket: "marine",
    icon: "Anchor",
    keywords: [
      "boat detailing", "mobile boat detailing", "boat wash and wax",
      "pontoon boat cleaning", "center console detailing",
      "fishing boat detailing", "sailboat detailing", "boat interior cabin detailing",
    ],
    relatedServices: ["marine-detailing", "boat-washing", "yacht-detailing"],
    faqs: [
      {
        q: "What's included in boat detailing in {city}?",
        a: "Approved {city}, FL boat detailers handle exterior wash, oxidation removal coordination, wax or sealant, vinyl + canvas cleaning, interior cabin vacuum + wipe-down, and optional ceramic-prep.",
      },
    ],
  },
  {
    slug: "yacht-detailing",
    label: "Yacht Detailing",
    serviceType: "Yacht Detailing",
    audience: "customer",
    bucket: "marine",
    icon: "Sailboat",
    keywords: [
      "yacht detailing", "yacht wash and wax", "yacht exterior detailing",
      "yacht interior detailing", "dockside yacht detailing", "yacht ceramic prep",
    ],
    relatedServices: ["marine-detailing", "marina-detailing-services", "boat-detailing"],
    faqs: [
      {
        q: "Can you detail my yacht while it's in the slip in {city}?",
        a: "Yes — approved {city}, FL marine vendors detail yachts in-slip, on the dry-stack, or at private docks. Larger vessels can be scheduled across multiple visits to match marina policies.",
      },
    ],
  },
  {
    slug: "marina-detailing-services",
    label: "Marina Detailing Services",
    serviceType: "Marina Detailing Services",
    audience: "fleet",
    bucket: "marine",
    icon: "Anchor",
    brand: "fleet",
    keywords: [
      "marina detailing services", "marina fleet wash",
      "dockside boat detailing", "marina contractor",
      "dry stack detailing", "boat club detailing partner",
    ],
    relatedServices: ["marine-detailing", "yacht-detailing", "fleet-washing"],
    faqs: [
      {
        q: "Do you serve marinas and boat clubs in {city}?",
        a: "Yes — WashPros™ partners with marinas, dry-stack facilities, and boat clubs in {city}, FL to offer member-direct detailing and bulk on-site service contracts.",
      },
    ],
  },
  {
    slug: "rv-washing",
    label: "RV Washing",
    serviceType: "RV Washing",
    audience: "customer",
    bucket: "rv",
    icon: "Truck",
    keywords: [
      "RV washing", "RV wash", "mobile RV wash", "camper washing",
      "motorhome washing", "travel trailer cleaning", "RV roof wash",
    ],
    relatedServices: ["rv-detailing", "camper-detailing", "motorhome-detailing"],
    faqs: [
      {
        q: "Can you wash my RV at my campground in {city}?",
        a: "Yes — approved {city}, FL RV vendors travel to campgrounds, RV parks, and home storage pads with water-conscious equipment that complies with most park rules.",
      },
    ],
  },
  {
    slug: "rv-detailing",
    label: "RV Detailing",
    serviceType: "RV Detailing",
    audience: "customer",
    bucket: "rv",
    icon: "Truck",
    keywords: [
      "RV detailing", "mobile RV detailing", "RV wash and wax",
      "RV exterior detailing", "RV interior detailing",
      "RV oxidation removal coordination", "RV ceramic coating prep",
      "fifth wheel detailing", "Class A motorhome detailing",
      "Class B camper van detailing", "Class C RV detailing",
      "toy hauler detailing",
    ],
    relatedServices: ["rv-washing", "camper-detailing", "motorhome-detailing"],
    faqs: [
      {
        q: "What does RV detailing in {city} cover?",
        a: "Approved {city}, FL vendors cover exterior wash + dry, oxidation-removal coordination, wax or sealant, slide-out seal cleaning, awning cleaning, interior vacuum + wipe, upholstery freshening, and roof inspection.",
      },
    ],
  },
  {
    slug: "camper-detailing",
    label: "Camper Detailing",
    serviceType: "Camper Detailing",
    audience: "customer",
    bucket: "rv",
    icon: "Truck",
    keywords: [
      "camper detailing", "camper washing", "camper van cleaning",
      "travel trailer cleaning", "pop-up camper detailing",
    ],
    relatedServices: ["rv-detailing", "motorhome-detailing", "rv-park-detailing-services"],
    faqs: [
      {
        q: "Do you detail camper vans in {city}?",
        a: "Yes — approved {city}, FL detailers handle Class B camper vans and travel trailers with the same care as larger motorhomes, including roof gentle-wash and vinyl-graphic-safe products.",
      },
    ],
  },
  {
    slug: "motorhome-detailing",
    label: "Motorhome Detailing",
    serviceType: "Motorhome Detailing",
    audience: "customer",
    bucket: "rv",
    icon: "Truck",
    keywords: [
      "motorhome detailing", "motorhome washing", "Class A motorhome detailing",
      "Class C RV detailing", "diesel pusher detailing",
    ],
    relatedServices: ["rv-detailing", "rv-washing", "rv-park-detailing-services"],
    faqs: [
      {
        q: "Can you detail a Class A motorhome at my storage lot in {city}?",
        a: "Yes — {city}, FL motorhome detailers travel to storage facilities, private driveways, and campgrounds with ladders, extension wands, and roof-safe gear for Class A coaches.",
      },
    ],
  },
  {
    slug: "rv-park-detailing-services",
    label: "RV Park Detailing Services",
    serviceType: "RV Park Detailing Services",
    audience: "fleet",
    bucket: "rv",
    icon: "Truck",
    brand: "fleet",
    keywords: [
      "RV park detailing services", "campground RV wash",
      "RV park vendor partnership", "campground detailing partner",
    ],
    relatedServices: ["rv-washing", "rv-detailing", "fleet-washing"],
    faqs: [
      {
        q: "Can WashPros set up a recurring on-site RV detailing day at our park in {city}?",
        a: "Yes — WashPros™ schedules recurring on-site service days at RV parks and campgrounds in {city}, FL where approved vendors offer member-direct washes and detailing during peak weekends.",
      },
    ],
  },
  // Specialty vehicle
  {
    slug: "golf-cart-cleaning",
    label: "Golf Cart Cleaning",
    serviceType: "Golf Cart Cleaning",
    audience: "customer",
    bucket: "specialty",
    icon: "Car",
    keywords: [
      "golf cart cleaning", "golf cart detailing", "golf cart wash",
      "neighborhood electric vehicle cleaning", "LSV detailing",
    ],
    relatedServices: ["mobile-detailing", "specialty-vehicle-detailing"],
    faqs: [
      {
        q: "Do you clean golf carts in gated communities in {city}?",
        a: "Yes — {city}, FL detailers travel to gated communities, golf clubs, and HOA-managed properties with golf-cart-safe products and battery-aware care.",
      },
    ],
  },
  {
    slug: "motorcycle-detailing",
    label: "Motorcycle Detailing",
    serviceType: "Motorcycle Detailing",
    audience: "customer",
    bucket: "specialty",
    icon: "Car",
    keywords: [
      "motorcycle detailing", "motorcycle wash", "Harley detailing",
      "sportbike detailing", "chrome polishing motorcycle",
    ],
    relatedServices: ["mobile-detailing", "specialty-vehicle-detailing"],
    faqs: [
      {
        q: "Can you detail a Harley in {city}?",
        a: "Yes — {city}, FL motorcycle detailers handle Harleys, sportbikes, baggers, and adventure bikes with chrome-safe polishes and bug-removal pre-treatments.",
      },
    ],
  },
  {
    slug: "specialty-vehicle-detailing",
    label: "Specialty Vehicle Detailing",
    serviceType: "Specialty Vehicle Detailing",
    audience: "customer",
    bucket: "specialty",
    icon: "Sparkles",
    keywords: [
      "specialty vehicle detailing", "classic car detailing",
      "exotic car detailing", "luxury car detailing",
      "off-road vehicle cleaning", "trailer detailing",
      // Specialty cluster (Rick T+34 Phase 7b)
      "ATV detailing", "UTV detailing",
      "trailer washing", "horse trailer detailing",
      "utility trailer washing",
      "black car detailing",
    ],
    relatedServices: ["mobile-detailing", "marine-detailing", "rv-detailing"],
    faqs: [
      {
        q: "Do you detail classic cars, exotics, or trailers in {city}?",
        a: "Yes — approved {city}, FL detailers offer specialty packages for classic cars, exotics, off-roaders, utility trailers, horse trailers, ATVs, UTVs, and other non-standard vehicles. Flag the make + condition during booking.",
      },
    ],
  },
  // Rideshare / gig-economy cleaning (Rick T+34 Phase 7b NEW SERVICE)
  {
    slug: "rideshare-vehicle-cleaning",
    label: "Rideshare Vehicle Cleaning",
    serviceType: "Rideshare Vehicle Cleaning",
    audience: "customer",
    bucket: "vehicle",
    icon: "Car",
    keywords: [
      "rideshare car detailing", "rideshare vehicle cleaning",
      "Turo car cleaning", "Turo detailing",
      "Uber car cleaning", "Lyft car cleaning",
      "rental car cleaning", "rideshare prep",
      "between-rentals car wash", "host turnaround detail",
      "Turo turnover cleaning",
    ],
    relatedServices: ["mobile-car-wash", "mobile-detailing", "fleet-washing"],
    faqs: [
      {
        q: "Can WashPros wash my Turo car between rentals in {city}?",
        a: "Yes — Turo, Uber, and Lyft drivers in {city}, FL use WashPros for fast turnaround cleans between rentals. Book a quick exterior wash + interior wipe or a full detail; the approved vendor comes to your driveway, lot, or rental staging area.",
      },
      {
        q: "Do you offer interior sanitization between rideshare trips?",
        a: "Yes. WashPros vendors in {city}, FL offer interior wipe-down, vacuum, leather conditioning, and odor neutralization as a quick rideshare-turnaround package so your next guest steps into a fresh vehicle.",
      },
      {
        q: "Is there a recurring plan for rental fleet hosts?",
        a: "Yes — Turo and rental hosts in {city}, FL can request recurring turnaround cleanings with consolidated billing through WashPros Fleet™.",
      },
    ],
  },
  // Dealership detailing (Rick T+34 Phase 7b NEW SERVICE — B2B)
  {
    slug: "dealership-detailing",
    label: "Dealership Detailing",
    serviceType: "Dealership Inventory Detailing",
    audience: "fleet",
    bucket: "fleet",
    icon: "Building2",
    brand: "fleet",
    keywords: [
      "dealership detailer", "mobile detailing for dealerships",
      "dealership inventory detailing", "dealership lot prep",
      "pre-delivery detail", "new car prep detailing",
      "used car inventory detailing", "dealership reconditioning",
      "auto dealer wash service",
    ],
    relatedServices: ["fleet-washing", "fleet-partner-opportunities", "mobile-detailing"],
    faqs: [
      {
        q: "Can WashPros handle dealership inventory detailing in {city}?",
        a: "Yes — WashPros Fleet™ partners with new and used car dealerships in {city}, FL for lot wash days, pre-delivery details, customer-pickup prep, and ongoing inventory reconditioning. One dispatch, approved vendors, consolidated invoicing.",
      },
      {
        q: "Do you do recurring dealership lot prep?",
        a: "Yes. Dealerships in {city}, FL can schedule recurring weekly or monthly lot prep with WashPros Fleet™. The vendor handles exterior wash, interior wipe-down, glass, tires, and dressing across your active inventory.",
      },
    ],
  },
  // Vendor + partner pages
  {
    slug: "vendor-opportunities",
    label: "Vendor Opportunities",
    serviceType: "Mobile Detailing Vendor Marketplace",
    audience: "vendor",
    bucket: "vendor",
    icon: "Briefcase",
    keywords: [
      "car wash vendor app", "mobile detailing vendor app",
      "detailing jobs near me", "mobile car wash jobs",
      "car wash contractor opportunities", "join WashPros as a vendor",
      "WashPros approved vendor", "make money detailing cars",
      "vendor marketplace for detailers", "detailing dispatch app",
    ],
    relatedServices: ["fleet-partner-opportunities", "mobile-car-wash", "mobile-detailing"],
    faqs: [
      {
        q: "How do I join WashPros as a vendor in {city}?",
        a: "Apply at washpros.io/become-a-vendor. The application captures your service area, equipment, business license, and references. Approved {city}, FL vendors get access to customer bookings, fleet contracts, and the WashPros dispatch app.",
      },
      {
        q: "Do I need my own equipment?",
        a: "Yes — approved WashPros vendors bring their own water, soap, microfiber, and equipment. The platform handles booking, payment, dispatch, customer chat, GPS routing, and reviews.",
      },
    ],
  },
  {
    slug: "fleet-partner-opportunities",
    label: "Fleet Partner Opportunities",
    serviceType: "Commercial Fleet Cleaning Partner Program",
    audience: "fleet",
    bucket: "fleet",
    icon: "Building2",
    brand: "fleet",
    keywords: [
      "fleet partner opportunity", "WashPros Fleet partner",
      "fleet wash partner", "commercial vehicle washing partner",
      "fleet detailing vendor", "Florida fleet detailing",
    ],
    relatedServices: ["fleet-washing", "vendor-opportunities", "marina-detailing-services"],
    faqs: [
      {
        q: "What's a WashPros Fleet partner?",
        a: "A WashPros Fleet™ partner is an approved commercial vendor or operator who handles repeat fleet contracts — corporate motor pools, delivery vans, dealership lots, rental fleets, municipal vehicles, marinas, and RV parks — through one dispatch interface.",
      },
    ],
  },
];

/* ─── Florida counties ──────────────────────────────────────────── */
export const FLORIDA_COUNTIES = [
  "Alachua", "Baker", "Bay", "Bradford", "Brevard", "Broward",
  "Calhoun", "Charlotte", "Citrus", "Clay", "Collier", "Columbia",
  "DeSoto", "Dixie", "Duval", "Escambia", "Flagler", "Franklin",
  "Gadsden", "Gilchrist", "Glades", "Gulf", "Hamilton", "Hardee",
  "Hendry", "Hernando", "Highlands", "Hillsborough", "Holmes",
  "Indian River", "Jackson", "Jefferson", "Lafayette", "Lake",
  "Lee", "Leon", "Levy", "Liberty", "Madison", "Manatee", "Marion",
  "Martin", "Miami-Dade", "Monroe", "Nassau", "Okaloosa",
  "Okeechobee", "Orange", "Osceola", "Palm Beach", "Pasco",
  "Pinellas", "Polk", "Putnam", "Santa Rosa", "Sarasota",
  "Seminole", "St. Johns", "St. Lucie", "Sumter", "Suwannee",
  "Taylor", "Union", "Volusia", "Wakulla", "Walton", "Washington",
];

/* ─── City catalogs by bucket ────────────────────────────────────
   Cities are grouped so each service only generates pages where it
   makes geographic sense (marine in coastal, RV in RV hubs, vehicle
   everywhere). Slugs are kebab-cased and ASCII-only.
   ───────────────────────────────────────────────────────────────── */

// Primary statewide vehicle cities (mobile-car-wash, mobile-detailing,
// fleet-washing, specialty-vehicle-detailing, vendor-opportunities,
// golf-cart-cleaning, motorcycle-detailing).
export const VEHICLE_CITIES = [
  "Miami", "Miami Beach", "Hialeah", "Doral", "Coral Gables", "Kendall",
  "Homestead", "Aventura", "North Miami", "Fort Lauderdale", "Hollywood",
  "Pembroke Pines", "Miramar", "Davie", "Weston", "Plantation",
  "Pompano Beach", "Boca Raton", "Delray Beach", "Boynton Beach",
  "West Palm Beach", "Palm Beach Gardens", "Jupiter", "Port St. Lucie",
  "Stuart", "Vero Beach", "Fort Pierce", "Naples", "Marco Island",
  "Bonita Springs", "Fort Myers", "Cape Coral", "Lehigh Acres",
  "Sarasota", "Bradenton", "Venice", "Tampa", "St. Petersburg",
  "Clearwater", "Brandon", "Riverview", "Wesley Chapel", "Lakeland",
  "Winter Haven", "Orlando", "Kissimmee", "Winter Park", "Lake Nona",
  "Sanford", "Altamonte Springs", "Daytona Beach", "Ormond Beach",
  "New Smyrna Beach", "Jacksonville", "St. Augustine", "Ponte Vedra",
  "Gainesville", "Tallahassee", "Pensacola", "Pensacola Beach", "Destin",
  "Fort Walton Beach", "Panama City", "Panama City Beach",
  "Rosemary Beach", "Seaside", "Ocala", "The Villages",
  "Clermont", "Melbourne", "Cocoa Beach", "Titusville",
  "Key West", "Key Largo", "Islamorada", "Marathon",
  // Phase 7g.15.108 (Rick 2026-06-16) — Panhandle expansion
  // (coastline + inland support cities west of Tallahassee).
  "Navarre", "Gulf Breeze", "Milton", "Pace", "Cantonment",
  "Crestview", "Niceville", "Mary Esther", "Mexico Beach",
  "Port St. Joe", "Apalachicola", "DeFuniak Springs", "Bonifay",
  "Chipley", "Marianna", "Quincy",
];

// Marine-relevant cities — coastal + inland markets where boat/RV
// owners live and search by their home city.  Phase 7e
// reconciliation (2026-05-11): expanded to match production's
// wider coverage.  Coastal cities remain primary marine geography;
// inland cities are kept under Search Console review (we'll prune
// based on real GSC data, never speculatively).
export const MARINE_CITIES = [
  // ── Coastal / dive / boat-club geography (Phase 7 original list)
  "Miami", "Miami Beach", "Key Biscayne", "Coconut Grove", "Coral Gables",
  "Aventura", "Fort Lauderdale", "Hollywood", "Pompano Beach",
  "Deerfield Beach", "Boca Raton", "Delray Beach", "West Palm Beach",
  "Palm Beach", "Jupiter", "Stuart", "Vero Beach", "Fort Pierce",
  "Port St. Lucie", "Key West", "Key Largo", "Islamorada", "Marathon",
  "Naples", "Marco Island", "Bonita Springs", "Fort Myers", "Cape Coral",
  "Sarasota", "Bradenton", "Venice", "Tampa", "St. Petersburg",
  "Clearwater", "Dunedin", "Tarpon Springs", "Daytona Beach",
  "New Smyrna Beach", "Cocoa Beach", "Melbourne", "Jacksonville",
  "St. Augustine", "Ponte Vedra Beach", "Pensacola", "Pensacola Beach",
  "Destin", "Fort Walton Beach", "Panama City", "Panama City Beach",
  "Rosemary Beach", "Seaside",
  // Phase 7g.15.108 (Rick 2026-06-16) — Panhandle coastal expansion.
  "Navarre", "Gulf Breeze", "Mary Esther", "Mexico Beach",
  "Port St. Joe", "Apalachicola",
  // ── Phase 7e inland-marine restoration (production sitemap parity).
  // Boat / yacht / jet-ski owners store equipment near their primary
  // residence and search by their home city.  Watch GSC click-through
  // rate before pruning any of these.
  "Altamonte Springs", "Boynton Beach", "Brandon", "Clermont",
  "Davie", "Doral", "Gainesville", "Hialeah", "Homestead",
  "Kendall", "Kissimmee", "Lake Nona", "Lakeland", "Lehigh Acres",
  "Miramar", "North Miami", "Ocala", "Orlando", "Ormond Beach",
  "Palm Beach Gardens", "Pembroke Pines", "Plantation", "Ponte Vedra",
  "Riverview", "Sanford", "Tallahassee", "The Villages", "Titusville",
  "Wesley Chapel", "Weston", "Winter Haven", "Winter Park",
];

// RV-relevant cities — RV park / snowbird / road-trip geography +
// Phase 7e expansion for full-state coverage (RV owners travel
// everywhere and search by their home city).
export const RV_CITIES = [
  // ── Phase 7 original (RV-park + snowbird targets)
  "Orlando", "Kissimmee", "Clermont", "The Villages", "Ocala", "Tampa",
  "St. Petersburg", "Clearwater", "Lakeland", "Winter Haven", "Sarasota",
  "Bradenton", "Venice", "Fort Myers", "Cape Coral", "Naples",
  "Marco Island", "Daytona Beach", "New Smyrna Beach", "St. Augustine",
  "Jacksonville", "Gainesville", "Tallahassee", "Pensacola", "Pensacola Beach", "Destin",
  "Panama City", "Panama City Beach", "Rosemary Beach", "Seaside",
  "Melbourne", "Cocoa Beach", "Port St. Lucie",
  "West Palm Beach", "Miami", "Fort Lauderdale", "Key West",
  // ── Phase 7e expansion (production sitemap parity).
  // RV / motorhome / camper owners drive — RV pages legitimately serve
  // every Florida metro.
  "Altamonte Springs", "Aventura", "Boca Raton", "Bonita Springs",
  "Boynton Beach", "Brandon", "Coral Gables", "Davie", "Delray Beach",
  "Doral", "Fort Pierce", "Fort Walton Beach", "Hialeah", "Hollywood",
  "Homestead", "Islamorada", "Jupiter", "Kendall", "Key Largo",
  "Lake Nona", "Lehigh Acres", "Marathon", "Miami Beach", "Miramar",
  "North Miami", "Ormond Beach", "Palm Beach Gardens", "Pembroke Pines",
  "Plantation", "Pompano Beach", "Ponte Vedra", "Riverview", "Sanford",
  "Stuart", "Titusville", "Vero Beach", "Wesley Chapel", "Weston",
  "Winter Park",
  // Phase 7g.15.108 (Rick 2026-06-16) — Panhandle expansion for RV
  // owners (RV parks + snowbird routes in the western Panhandle).
  "Navarre", "Gulf Breeze", "Milton", "Pace", "Cantonment",
  "Crestview", "Niceville", "Mary Esther", "Mexico Beach",
  "Port St. Joe", "Apalachicola", "DeFuniak Springs", "Bonifay",
  "Chipley", "Marianna", "Quincy",
];

// City-hub cities — every Florida city for which we serve a bare
// `/florida/{city}` landing page.  Used by the sitemap enumerator
// + the upcoming FloridaCityHubPage renderer.  Union of all per-
// bucket city lists.  Phase 7e (2026-05-11).
export const CITY_HUB_CITIES = Array.from(
  new Set([...VEHICLE_CITIES, ...MARINE_CITIES, ...RV_CITIES])
).sort();

// Per-service-bucket → city list mapping
export const SERVICE_CITY_MATRIX = {
  vehicle: VEHICLE_CITIES,
  fleet: VEHICLE_CITIES,
  marine: MARINE_CITIES,
  rv: RV_CITIES,
  specialty: VEHICLE_CITIES,
  vendor: VEHICLE_CITIES,
};

/* ─── Helpers ────────────────────────────────────────────────────── */
export const toCitySlug = (city) =>
  String(city || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[.']/g, "")
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");

export const toCountySlug = (county) => toCitySlug(county);

export const fromCitySlug = (slug) => {
  // Reverse-lookup. We don't want to leak unknown city slugs to the
  // SEO surface (they'd render empty pages), so callers should always
  // validate the slug against this set.
  const all = new Set([...VEHICLE_CITIES, ...MARINE_CITIES, ...RV_CITIES]);
  for (const c of all) {
    if (toCitySlug(c) === slug) return c;
  }
  return null;
};

export const getServiceBySlug = (slug) =>
  SERVICES.find((s) => s.slug === slug) || null;

export const isCityValidForService = (citySlug, serviceSlug) => {
  const svc = getServiceBySlug(serviceSlug);
  if (!svc) return false;
  const cities = SERVICE_CITY_MATRIX[svc.bucket] || [];
  return cities.some((c) => toCitySlug(c) === citySlug);
};

/* ─── Page URL builders (canonical only) ─────────────────────────── */
export const buildStatewideUrl = (serviceSlug) =>
  `${CANONICAL_DOMAIN}/florida/${serviceSlug}`;

export const buildCityUrl = (citySlug, serviceSlug) =>
  `${CANONICAL_DOMAIN}/florida/${citySlug}/${serviceSlug}`;

export const buildCountyUrl = (countySlug) =>
  `${CANONICAL_DOMAIN}/florida/${countySlug}-county`;

/* ─── Spanish alternate-name map (Rick T+34 Phase 7b) ─────────────
   These are NOT URL slugs — we never duplicate the canonical URL
   space with Spanish paths (that would dilute SEO authority and
   fight our existing i18n bundle).  Instead, these strings flow
   into Schema.org Service.alternateName JSON-LD entries so
   Googlebot has explicit hreflang-equivalent signal that the
   English landing page also targets the Spanish search query.
   ───────────────────────────────────────────────────────────────── */
export const SPANISH_ALTERNATE_NAMES = {
  "mobile-car-wash": "Lavado de carros a domicilio",
  "mobile-detailing": "Detallado de autos a domicilio",
  "fleet-washing": "Lavado de flota comercial",
  "marine-detailing": "Detallado marino móvil",
  "boat-washing": "Lavado de botes a domicilio",
  "boat-detailing": "Detallado de botes",
  "yacht-detailing": "Lavado de yates",
  "marina-detailing-services": "Servicios de detallado para marinas",
  "rv-washing": "Lavado de RV / casa rodante",
  "rv-detailing": "Detallado de RV / casa rodante",
  "camper-detailing": "Detallado de campers",
  "motorhome-detailing": "Detallado de motorhomes",
  "rv-park-detailing-services": "Detallado en parques de RV",
  "golf-cart-cleaning": "Limpieza de carritos de golf",
  "motorcycle-detailing": "Detallado de motos",
  "specialty-vehicle-detailing": "Detallado de vehículos especiales",
  "vendor-opportunities": "Oportunidades para proveedores de lavado de autos",
  "fleet-partner-opportunities": "Socio comercial de WashPros Fleet",
  "rideshare-vehicle-cleaning": "Limpieza de autos para Turo, Uber y Lyft",
  "dealership-detailing": "Detallado para concesionarios de autos",
};

/* ─── Supplemental keyword clusters (Rick T+34 Phase 7b) ──────────
   Internal-only keyword inventory documenting the seven natural
   search-intent clusters Rick supplied on 2026-05-11.  These are
   already woven into the relevant SERVICES[*].keywords arrays
   above for internal copy variation — never emitted as a
   <meta name="keywords"> tag (Google ignores it and it looks
   spammy).  Keeping this constant exported so future content +
   ad campaigns can pull from the same canonical list.
   ───────────────────────────────────────────────────────────────── */
export const SUPPLEMENTAL_KEYWORD_CLUSTERS = {
  urgent_convenience: [
    "same-day mobile car wash", "same-day mobile detailing",
    "emergency car detailing", "last-minute car wash",
    "car wash at work", "office car wash service",
    "workplace fleet washing",
    "apartment car wash service", "condo car wash service",
    "HOA car wash service",
    "hotel valet car wash", "airport car detailing",
  ],
  marketplace_gig: [
    "Turo car cleaning", "rental car cleaning",
    "rideshare car detailing",
    "Uber car cleaning", "Lyft car cleaning",
  ],
  deep_detailing: [
    "odor removal car detailing", "pet hair removal car detailing",
    "smoke smell removal car", "stain removal car seats",
    "leather seat cleaning", "clay bar treatment",
    "ceramic coating prep", "paint protection prep",
    "headlight restoration", "engine bay cleaning",
    "car wash gift card", "car detailing gift card",
  ],
  commercial_fleet: [
    "box truck washing", "sprinter van detailing",
    "delivery van washing", "Amazon van washing",
    "food truck washing", "semi truck detailing",
    "shuttle bus washing", "school van cleaning",
    "church bus washing", "limo detailing", "party bus cleaning",
    "police fleet washing", "municipal fleet washing",
    "medical transport vehicle cleaning",
    "dealership inventory detailing",
  ],
  specialty_vehicles: [
    "classic car detailing", "exotic car detailing",
    "luxury car detailing", "black car detailing",
    "motorcycle detailing near me", "golf cart detailing near me",
    "ATV detailing", "UTV detailing",
    "trailer washing", "horse trailer detailing",
  ],
  marine_rv_near_me: [
    "boat wash near me", "mobile boat detailing near me",
    "yacht wash near me", "jet ski detailing near me",
    "RV wash near me", "mobile RV wash near me",
    "camper wash near me", "motorhome detailing near me",
  ],
  spanish: [
    "lavado de carros a domicilio", "lavado de autos a domicilio",
    "detallado de carros", "detallado de autos a domicilio",
    "car wash móvil", "lavado móvil de carros",
    "limpieza de carros a domicilio", "lavado de camionetas",
    "lavado de botes", "detallado de botes",
    "lavado de yates", "lavado de RV", "detallado de RV",
    "trabajos de lavado de carros", "trabajos de detailing",
    "ganar dinero lavando carros", "proveedor de lavado de autos",
  ],
};
